/**
 * Editing the location this screen is about.
 *
 * Every field on the screen writes back through the same path — replace the
 * whole location on the shipment and mark the screen dirty — so the setters
 * live together rather than being spelled out at each input. Which half of the
 * shipment gets written is the only thing that varies.
 */

import { useLocationsAddressOptions } from "@/Shared/Locations/Hooks/useLocationsAddressOptions";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import type { Patch } from "@freightsimple/shared/types";
import type { Address } from "@freightsimple/generated-dashboard-openapi-client/models/Address";
import type { PreQuoteLocation } from "@freightsimple/generated-dashboard-openapi-client/models/PreQuoteLocation";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { useDispatch } from "react-redux";
import {
  setCurrentScreenIsDirty,
  setDeliveryLocation,
  setPickupLocation,
} from "@/Slices/bookShipmentSlice";

export function useBookShipmentAddressLocationEdits(
  context: LocationContext,
  location: PreQuoteLocation,
) {
  const dispatch = useDispatch();

  function updateLocation(updatedLocation: AddLocationLocationType) {
    dispatch(setCurrentScreenIsDirty());
    if (context === LocationContext.Pickup) {
      dispatch(setPickupLocation(updatedLocation));
    } else {
      dispatch(setDeliveryLocation(updatedLocation));
    }
  }

  function updateAddress(addressUpdates: Patch<Address>) {
    const newLocation = {
      ...location,
      address: {
        ...location.address,
        ...addressUpdates,
      },
    };

    updateLocation(newLocation);
  }

  const { addressOptions } = useLocationsAddressOptions(
    location,
    updateAddress,
  );

  function setBusinessName(businessName: string) {
    updateLocation({
      ...location,
      businessName,
    });
  }

  function setNotes(notes: string) {
    updateLocation({
      ...location,
      notes,
    });
  }

  function setHoursOpenFrom(openFrom: string) {
    updateLocation({
      ...location,
      hours: {
        ...location.hours,
        openFrom,
      },
    });
  }

  function setHoursOpenUntil(openUntil: string) {
    updateLocation({
      ...location,
      hours: {
        ...location.hours,
        openUntil,
      },
    });
  }

  return {
    addressOptions,
    updateAddress,
    setBusinessName,
    setNotes,
    setHoursOpenFrom,
    setHoursOpenUntil,
  };
}
